import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "../../axios";
import DashboardLayout from "../components/dashboard_layout";
import {
  Box,
  Card,
  Grid,
  Icon,
  Typography,
  CircularProgress,
  Alert,
  Snackbar,
  Backdrop,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  TextField,
  ListItem,
  ListItemText,
  ListItemIcon,
} from "@mui/material";
import { useAuth } from "../../auth_provider";

function DashboardPage() {
  const navigate = useNavigate();
  const auth = useAuth();

  const [showLoading, setShowLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [errorMsg, setErrMsg] = useState("");
  const [severity, setSeverity] = useState("");
  const handleClose = () => {
    setOpen(false);
    setErrMsg("");
  };

  const [refreshPage, setRefreshPage] = useState(false);

  const [debts, setDebts] = useState([]);
  const [selectedDebt, setSelectedDebt] = useState({});
  const [credits, setCredits] = useState([]);
  const [changePasswordPrompt, setChangePasswordPrompt] = useState(false);
  const [paymentInstrument, setPaymentInstrument] = useState(null);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [currentPassword, setCurrentPassword] = useState(null);
  const [newPassword, setPassword] = useState(null);
  const [cPassword, setCPassword] = useState(null);
  const [showPwdDialog, setShowPwdDialog] = useState(false);

  const [emailField, setEmailField] = useState(null);
  const [numberField, setNumberField] = useState(null);
  const [otpField, setOtpField] = useState(null);
  const [otp, setOTP] = useState(null);
  const [showTSADialog, setShowTSADialog] = useState(false);
  const [showOTPDialog, setShowOTPDialog] = useState(false);
  const [checkingState, setCheckingState] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setShowLoading(true);
      await axios
        .get(`practitioners/dashboard.json`)
        .then((result) => {
          console.log(result.data.data);
          setShowLoading(false);
          if (result.data.status === "success") {
            setDebts(result.data.data.debts);
            setCredits(result.data.data.credits);
            setChangePasswordPrompt(result.data.data.pwd);
          } else {
            setSeverity("error");
            setErrMsg(result.data.data.message);
            setOpen(true);
          }
        })
        .catch(() => {
          setShowLoading(false);
          setSeverity("error");
          setErrMsg("An error occurred.");
          setOpen(true);
        });
    };

    fetchData();
  }, []);

  useEffect(() => {
    if (changePasswordPrompt) {
      setTimeout(() => {
        setShowPwdDialog(true);
      }, 1000);
    }
  }, [changePasswordPrompt]);

  async function confirmOTP() {
    if (otpField === null) {
      setSeverity("error");
      setErrMsg("Please enter the OTP sent to your email");
      setOpen(true);
      return;
    }

    if (otp === null) {
      setSeverity("error");
      setErrMsg("Possible malicious behaviour detected.");
      setOpen(true);
      return;
    }

    if (otpField !== otp) {
      setSeverity("error");
      setErrMsg("Invalid OTP entered.");
      setOpen(true);
      return;
    }

    changePassword();
  }

  async function getOTP() {
    if (emailField === null) {
      setSeverity("error");
      setErrMsg("Please enter a valid email");
      setOpen(true);
      return;
    }

    if (numberField === null) {
      setSeverity("error");
      setErrMsg("Please enter a valid phone number");
      setOpen(true);
      return;
    }

    if (currentPassword === null) {
      setSeverity("error");
      setErrMsg("Please enter your current password");
      setOpen(true);
      return;
    }

    if (newPassword === null) {
      setSeverity("error");
      setErrMsg("Please enter your new password");
      setOpen(true);
      return;
    }

    if (cPassword === null) {
      setSeverity("error");
      setErrMsg("Please repeat your new password");
      setOpen(true);
      return;
    }

    if (cPassword !== newPassword) {
      setSeverity("error");
      setErrMsg("Your new passwords do not match");
      setOpen(true);
      return;
    }

    setShowPwdDialog(false);
    setShowLoading(true);
    const json = {
      email_address: emailField,
      phone_number: numberField,
      password: currentPassword,
      npassword: newPassword,
      cpassword: cPassword,
    };

    await axios
      .post("practitioners/generate/otp.json", json)
      .then((result) => {
        setShowLoading(false);
        if (result.data.status === "success") {
          setOTP(result.data.data.otp);
          setShowOTPDialog(true);
        } else {
          setSeverity("error");
          setErrMsg(result.data.data.message);
          setOpen(true);

          setShowPwdDialog(true);
        }
      })
      .catch(() => {
        setShowLoading(false);
        setSeverity("error");
        setErrMsg("An error occurred.");
        setOpen(true);
        setShowPwdDialog(true);
      });
  }

  async function changePassword() {
    if (emailField === null) {
      setSeverity("error");
      setErrMsg("Please enter a valid email");
      setOpen(true);
      return;
    }

    if (numberField === null) {
      setSeverity("error");
      setErrMsg("Please enter a valid phone number");
      setOpen(true);
      return;
    }

    if (currentPassword === null) {
      setSeverity("error");
      setErrMsg("Please enter your current password");
      setOpen(true);
      return;
    }

    if (newPassword === null) {
      setSeverity("error");
      setErrMsg("Please enter your new password");
      setOpen(true);
      return;
    }

    if (cPassword === null) {
      setSeverity("error");
      setErrMsg("Please repeat your new password");
      setOpen(true);
      return;
    }

    if (cPassword !== newPassword) {
      setSeverity("error");
      setErrMsg("Your new passwords do not match");
      setOpen(true);
      return;
    }

    setShowOTPDialog(false);
    setShowLoading(true);
    const json = {
      email_address: emailField,
      phone_number: numberField,
      password: currentPassword,
      npassword: newPassword,
      cpassword: cPassword,
    };

    await axios
      .post("practitioners/password/change.json", json)
      .then((result) => {
        setShowLoading(false);
        if (result.data.status === "success") {
          setSeverity("success");
          setErrMsg(result.data.data.message);
          setOpen(true);

          setRefreshPage(!refreshPage);
        } else {
          setSeverity("error");
          setErrMsg(result.data.data.message);
          setOpen(true);
          setShowPwdDialog(true);
        }
      })
      .catch(() => {
        setShowLoading(false);
        setSeverity("error");
        setErrMsg("An error occurred.");
        setOpen(true);
      });
  }

  async function generateDebtInstrument(reference) {
    setShowLoading(true);
    await axios
      .get(`practitioners/debts/${reference}/instrument.json`)
      .then((result) => {
        setShowLoading(false);
        if (result.data.status === "success") {
          setPaymentInstrument(result.data.data.instrument);
          setShowPaymentDialog(true);
        } else {
          setSeverity("error");
          setErrMsg(result.data.data.message);
          setOpen(true);
        }
      })
      .catch(() => {
        setShowLoading(false);
        setSeverity("error");
        setErrMsg("An error occurred.");
        setOpen(true);
      });
  }

  async function checkDebtPayment(reference) {
    setCheckingState(true);
    await axios
      .get(`practitioners/debts/${reference}/payment/check.json`)
      .then((result) => {
        setCheckingState(false);
        if (result.data.status === "success") {
          let sever = "info";
          if (result.data.data.status) {
            sever = "success";
            setShowPaymentDialog(true);
          }

          setSeverity(sever);
          setErrMsg(
            result.data.data.status
              ? "Paymet confirmed"
              : "Awaiting payment confirmation."
          );
          setOpen(true);
        } else {
          setSeverity("error");
          setErrMsg(result.data.data.message);
          setOpen(true);
        }
      })
      .catch(() => {
        setCheckingState(false);
        setSeverity("error");
        setErrMsg("An error occurred.");
        setOpen(true);
      });
  }

  return (
    <DashboardLayout>
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={showLoading}
        onClick={handleClose}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <Snackbar
        open={open}
        autoHideDuration={6000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={handleClose} severity={severity} sx={{ width: "100%" }}>
          {errorMsg}
        </Alert>
      </Snackbar>
      <Dialog open={showOTPDialog} fullWidth maxWidth="xs">
        <DialogTitle>Confirm OTP</DialogTitle>
        <DialogContent>
          <Box mt={3} mb={3}>
            <TextField
              onChange={(e) => setOtpField(e.target.value)}
              fullWidth
              label="Enter OTP"
              type="text"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          {/* <Button
            onClick={() => {
              setShowOTPDialog(false);
            }}
          >
            Cancel
          </Button>
          &nbsp;&nbsp; */}
          <Button
            onClick={() => confirmOTP()}
            color="primary"
            variant="contained"
          >
            Confirm OTP
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={showPwdDialog} fullWidth maxWidth="xs">
        <DialogTitle>Complete Profile Setup</DialogTitle>
        <DialogContent>
          <Box mt={3} mb={3}>
            <TextField
              onChange={(e) => setEmailField(e.target.value)}
              fullWidth
              label="Business Email Address"
              type="email"
            />
          </Box>
          <Box mt={3} mb={3}>
            <TextField
              onChange={(e) => setNumberField(e.target.value)}
              fullWidth
              label="Business Phone Number"
              type="text"
            />
          </Box>
          <Divider />
          <Box mt={3} mb={3}>
            <TextField
              onChange={(e) => setCurrentPassword(e.target.value)}
              fullWidth
              label="Current Password"
              type="password"
            />
          </Box>
          <Box mb={3}>
            <TextField
              onChange={(e) => setPassword(e.target.value)}
              fullWidth
              label="New Password"
              type="password"
            />
          </Box>
          <Box mb={3}>
            <TextField
              onChange={(e) => setCPassword(e.target.value)}
              fullWidth
              label="Confirm Password"
              type="password"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          {/* <Button
            onClick={() => {
              setShowPwdDialog(false);
            }}
          >
            Cancel
          </Button>
          &nbsp;&nbsp; */}
          <Button onClick={() => getOTP()} color="primary" variant="contained">
            Submit
          </Button>
        </DialogActions>
      </Dialog>
      {paymentInstrument ? (
        <Dialog open={showPaymentDialog} fullWidth maxWidth="xs">
          <DialogTitle>Debt Repayment</DialogTitle>
          <DialogContent>
            <Box mb={1}>
              <Typography>
                Make payment to the bank account details below.
              </Typography>
            </Box>
            <Box mb={3}>
              <Divider />
            </Box>
            <Box>
              <Box display="flex" justifyContent="space-between" mb={3}>
                <Box>
                  <Typography>Bank:</Typography>
                </Box>
                <Box>
                  <Typography>{paymentInstrument.bank}</Typography>
                </Box>
              </Box>
              <Box display="flex" justifyContent="space-between" mb={3}>
                <Box>
                  <Typography>Account Name:</Typography>
                </Box>
                <Box>
                  <Typography>{paymentInstrument.account_name}</Typography>
                </Box>
              </Box>
              <Box display="flex" justifyContent="space-between" mb={3}>
                <Box>
                  <Typography>Account Number:</Typography>
                </Box>
                <Box>
                  <Typography>{paymentInstrument.account_number}</Typography>
                </Box>
              </Box>
              <Box display="flex" justifyContent="space-between" mb={3}>
                <Box>
                  <Typography>Debt:</Typography>
                </Box>
                <Box>
                  <Typography>
                    ₦
                    {(selectedDebt.amount / 100).toLocaleString("en", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </Typography>
                </Box>
              </Box>
              <Box display="flex" justifyContent="space-between" mb={3}>
                <Box>
                  <Typography>Fee:</Typography>
                </Box>
                <Box>
                  <Typography>
                    ₦
                    {(selectedDebt.fee / 100).toLocaleString("en", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </Typography>
                </Box>
              </Box>
              <Box display="flex" justifyContent="space-between" mb={3}>
                <Box>
                  <Typography>Amount Payable:</Typography>
                </Box>
                <Box>
                  <Typography>
                    ₦
                    {((selectedDebt.amount + selectedDebt.fee) / 100).toLocaleString("en", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </Typography>
                </Box>
              </Box>
            </Box>
            <Box mb={3}>
              <Divider />
            </Box>
            <Box align="center" mb={3}>
              <Typography variant="caption">
                Your transaction will automatically complete once payment is
                confirmed
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setShowPaymentDialog(false);
              }}
            >
              Cancel
            </Button>
            &nbsp;&nbsp;
            <Box>
              {checkingState ? (
                <CircularProgress color="inherit" />
              ) : (
                <Button
                  color="success"
                  variant="contained"
                  onClick={() => {
                    checkDebtPayment(selectedDebt.reference);
                  }}
                >
                  I have made payment
                </Button>
              )}
            </Box>
          </DialogActions>
        </Dialog>
      ) : (
        <> </>
      )}
      <Dialog open={showTSADialog} fullWidth maxWidth="xs">
        <DialogTitle>Debt Repayment</DialogTitle>
        <DialogContent>
          <Box mb={1}>
            <Typography>
              Make payment to the bank account details below.
            </Typography>
          </Box>
          <Box mb={3}>
            <Divider />
          </Box>
          <Box>
            <Box display="flex" justifyContent="space-between" mb={3}>
              <Box>
                <Typography>Bank:</Typography>
              </Box>
              <Box>
                <Typography>Access Bank</Typography>
              </Box>
            </Box>
            <Box display="flex" justifyContent="space-between" mb={3}>
              <Box>
                <Typography>Account Name:</Typography>
              </Box>
              <Box>
                <Typography>LASAA Billboard</Typography>
              </Box>
            </Box>
            <Box display="flex" justifyContent="space-between" mb={3}>
              <Box>
                <Typography>Account Number:</Typography>
              </Box>
              <Box>
                <Typography>0055914693</Typography>
              </Box>
            </Box>
            <Box display="flex" justifyContent="space-between" mb={3}>
              <Box>
                <Typography>Amount Payable:</Typography>
              </Box>
              <Box>
                <Typography>
                  ₦
                  {(selectedDebt.amount / 100).toLocaleString("en", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </Typography>
              </Box>
            </Box>
          </Box>
          <Box mb={3}>
            <Divider />
          </Box>
          <Box align="center" mb={3}>
            <Typography variant="caption">
              This account is a <b>TSA</b> collection account. Funds sent to this account in error are non-refundable.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setShowTSADialog(false);
            }}
          >
            Cancel
          </Button>
          {/* &nbsp;&nbsp;
          <Box>
            {checkingState ? (
              <CircularProgress color="inherit" />
            ) : (
              <Button
                color="success"
                variant="contained"
                onClick={() => {
                  checkDebtPayment(selectedDebt.reference);
                }}
              >
                Upload Payment Receipt
              </Button>
            )}
          </Box> */}
        </DialogActions>
      </Dialog>
      {changePasswordPrompt ? (
        <Box my={3} pr={3}>
          <Alert severity="warning" sx={{ width: "100%" }}>
            Your profile setup is not complete
            <br />
            <Typography
              component="button"
              variant="contained"
              onClick={() => setShowPwdDialog(true)}
            >
              <b>Setup Now</b>
            </Typography>
          </Alert>
        </Box>
      ) : (
        <> </>
      )}
      <Box mt={6} align="right" px={1}>
        <Typography>
          Welcome <b>{auth.user.title}</b>
        </Typography>
      </Box>

      <Box mt={3}>
        <Grid container spacing={5}>
          <Grid item xs={6} md={6}>
            <Card
              elevation={2}
              onClick={() => {
                navigate("/apply");
              }}
            >
              <Box p={3}>
                <Icon color="primary">dynamic_form</Icon>
                <Typography>New Application</Typography>
              </Box>
            </Card>
          </Grid>
          <Grid item xs={6} md={6}>
            <Card
              elevation={2}
              onClick={() => {
                navigate("/applications");
              }}
            >
              <Box p={3}>
                <Icon color="info">list</Icon>
                <Typography>My Applications</Typography>
              </Box>
            </Card>
          </Grid>
          <Grid item xs={12} md={12}>
            <Card elevation={2}>
              <Box p={3}>
                <Icon color="error">list_alt</Icon>
                <Typography>Outstanding Debts:</Typography>
                <Box mt={3}>
                  {debts.map((debt, index) => (
                    <Box>
                      <ListItem>
                        <ListItemText
                          primary={`${index + 1}. ₦${(
                            debt.amount / 100
                          ).toLocaleString("en", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}`}
                        />
                        <Button
                          startIcon={<Icon>payment</Icon>}
                          onClick={() => {
                            setSelectedDebt(debt);
                            if (debt.type !== "SLP") {
                              setShowTSADialog(true);
                            } else {
                              generateDebtInstrument(debt.reference);
                            }
                          }}
                          size="small"
                          variant="contained"
                          color={debt.type !== "SLP" ? "warning" : "success"}
                        >
                          {debt.type !== "SLP" ? "Clear Debt" : "Pay Now"}
                        </Button>
                      </ListItem>
                      <Divider />
                    </Box>
                  ))}
                  {debts.length <= 0 ? (
                    <Typography fontSize={13}>No outstanding debts</Typography>
                  ) : (
                    <> </>
                  )}
                </Box>
              </Box>
            </Card>
          </Grid>
          <Grid item xs={12} md={12}>
            <Card elevation={2}>
              <Box p={3}>
                <Icon color="success">wallet</Icon>
                <Typography>Balances</Typography>
                <Box mt={3}>
                  {credits.map((line) => (
                    <Box>
                      <ListItemText
                        primary={`${line.type}: ₦
                    ${(line.balance / 100).toLocaleString("en", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}`}
                      />
                    </Box>
                  ))}
                </Box>
              </Box>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </DashboardLayout>
  );
}

export default DashboardPage;
